// Shared admin helpers — imported by every Tab component.
// Single source of truth for API, auth headers, URL resolution, and the
// reusable image/video upload field.
import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SITE_URL =
  process.env.REACT_APP_BACKEND_URL?.replace("/api", "").replace("api/", "") ||
  (typeof window !== "undefined" ? window.location.origin : "");

export const authHeaders = () => ({ withCredentials: true });

export const resolveUrl = (url) => {
  if (!url) return "";
  if (url.includes("/api/files/") && !url.startsWith(process.env.REACT_APP_BACKEND_URL)) {
    const filePath = url.substring(url.indexOf("/api/files/"));
    return `${process.env.REACT_APP_BACKEND_URL}${filePath}`;
  }
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
};

export const ImageUploadField = ({
  currentUrl,
  onUrlChange,
  onMediaTypeChange,
  label = "Image",
  acceptVideo = false,
}) => {
  const [uploading, setUploading] = useState(false);
  const [mode, setMode] = useState("upload");
  const fileRef = useRef(null);

  const maxSize = acceptVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
  const acceptStr = acceptVideo ? "image/*,video/*" : "image/*";

  const doUpload = async (file) => {
    if (!file) return;
    if (file.size > maxSize) {
      toast.error(`File too large. Max ${acceptVideo ? "50MB" : "10MB"}.`);
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/admin/upload`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUrlChange(res.data.url);
      if (onMediaTypeChange)
        onMediaTypeChange(file.type.startsWith("video/") ? "video" : "image");
      toast.success("Uploaded!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleChange = (e) => {
    const file = e.target.files?.[0];
    if (file) doUpload(file);
  };

  const isVideo =
    currentUrl &&
    (currentUrl.includes(".mp4") ||
      currentUrl.includes(".mov") ||
      currentUrl.includes(".webm"));

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 mb-1.5">
        <label className="font-sans text-xs text-brand-text-muted">{label}</label>
        <div className="flex gap-1 ml-auto">
          <button
            type="button"
            onClick={() => setMode("url")}
            data-testid="image-mode-url"
            className={`font-sans text-[10px] px-2 py-0.5 border transition-colors ${
              mode === "url"
                ? "border-brand-gold text-brand-gold"
                : "border-brand-border text-brand-text-muted"
            }`}
          >
            URL
          </button>
          <button
            type="button"
            onClick={() => setMode("upload")}
            data-testid="image-mode-upload"
            className={`font-sans text-[10px] px-2 py-0.5 border transition-colors ${
              mode === "upload"
                ? "border-brand-gold text-brand-gold"
                : "border-brand-border text-brand-text-muted"
            }`}
          >
            Upload
          </button>
        </div>
      </div>
      {mode === "url" ? (
        <Input
          value={currentUrl}
          onChange={(e) => onUrlChange(e.target.value)}
          placeholder={`Paste ${acceptVideo ? "image/video" : "image"} URL...`}
          data-testid="image-url-input"
          className="bg-brand-bg border-brand-border text-brand-text rounded-none"
        />
      ) : (
        <div>
          <input
            ref={fileRef}
            type="file"
            accept={acceptStr}
            onChange={handleChange}
            disabled={uploading}
            data-testid="image-file-input"
            className="block w-full text-sm text-brand-text-muted file:mr-4 file:py-2.5 file:px-4 file:border file:border-brand-border file:text-sm file:font-sans file:font-semibold file:bg-brand-surface file:text-brand-gold hover:file:bg-brand-gold hover:file:text-black file:cursor-pointer file:transition-colors file:rounded-none cursor-pointer"
          />
          {uploading && (
            <p className="font-sans text-xs text-brand-gold mt-1 flex items-center gap-1">
              <Loader2 size={12} className="animate-spin" /> Uploading...
            </p>
          )}
        </div>
      )}
      {currentUrl && (
        <div className="mt-2 border border-brand-border p-1 inline-block">
          {isVideo ? (
            <video
              src={resolveUrl(currentUrl)}
              className="h-16 w-24 object-cover"
              muted
            />
          ) : (
            <img
              src={resolveUrl(currentUrl)}
              alt="Preview"
              className="h-16 w-16 object-cover"
            />
          )}
        </div>
      )}
    </div>
  );
};
